import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

import { ApiService } from 'src/app/shared';

import { GatewayResolutionDto } from './dto/gateway-resolution.dto';
import { ResolvedGatewayDto } from './dto/resolved-gateway.dto';
import { ResolvedPaymentTypeDto } from './dto/resolved-payment-type.dto';
import { TokenizeGatewayDto } from './dto/tokenize-gateway.dto';

/**
 * Per-transaction gateway/merchant resolution, for PRECZN TOKENIZATION ONLY.
 *
 * Ported from the Member Portal onto the Backoffice SPA; the selection rule below is
 * architecture I7 rather than the Portal's superseded merchant-binding derivation.
 *
 * This service does NOT answer "is ACH offered". That determination is server-side, in
 * PaymentTypeDto.ValidAch / RevenueItemDto.ValidAch, and is consumed directly by the ACH-offering
 * surfaces (profile-edit-billing, pledge-edit-billing). Deriving ACH availability from anything on
 * this service would be the second capability determination the design forbids.
 *
 * Direct-gateway surfaces - every Backoffice site whose selected payment type already names a
 * paying CCGatewayID - bypass this service entirely and use
 * `PrecznService.resolveTokenizeGateway()` instead. Only the two event-registration wizards, whose
 * gateway follows from the selected revenue items, resolve through here.
 */
@Injectable({ providedIn: 'root' })
export class GatewayResolutionService {
  private _resolution$ = new BehaviorSubject<GatewayResolutionDto>(GatewayResolutionService.empty());

  constructor(private _apiService: ApiService) {}

  public observeResolution(): Observable<GatewayResolutionDto> {
    return this._resolution$.asObservable();
  }

  /** The gateway a card widget on this surface must tokenize against, or null. */
  public observeTokenizeGatewayForCard(): Observable<TokenizeGatewayDto | null> {
    return this._resolution$.pipe(
      map((resolution) => GatewayResolutionService.selectTokenizeGateway(resolution, (paymentType) => paymentType.CreditCardType === true)),
      distinctUntilChanged(GatewayResolutionService.gatewaysEqual)
    );
  }

  /** The gateway an ACH widget on this surface must tokenize against, or null. */
  public observeTokenizeGatewayForAch(): Observable<TokenizeGatewayDto | null> {
    return this._resolution$.pipe(
      map((resolution) => GatewayResolutionService.selectTokenizeGateway(resolution, (paymentType) => paymentType.AchType === true)),
      distinctUntilChanged(GatewayResolutionService.gatewaysEqual)
    );
  }

  public resolveForRevenueItems(itemNums: number[]): Promise<GatewayResolutionDto> {
    const distinctPositiveItemNums = Array.from(new Set((itemNums ?? []).filter((itemNum) => itemNum > 0)));

    if (distinctPositiveItemNums.length === 0) {
      const empty = GatewayResolutionService.empty();
      this._resolution$.next(empty);
      return Promise.resolve(empty);
    }

    return this._apiService
      .post('Revenue/GatewayResolutionForRevenueItems', distinctPositiveItemNums)
      .then((resolution: GatewayResolutionDto) => {
        this._resolution$.next(resolution);
        return resolution;
      })
      .catch(() => {
        // A transient failure publishes the empty resolution, so the observables emit null and the
        // widget does not enter Preczn mode. It must NOT fall back to the unparameterised
        // client-config merchant - nothing resolves means nothing is assumed (I7 step 3).
        const empty = GatewayResolutionService.empty();
        this._resolution$.next(empty);
        return empty;
      });
  }

  private static empty(): GatewayResolutionDto {
    return { Gateways: [], ItemNumsWithoutGateway: [] };
  }

  /**
   * I7's selection rule: among the resolved gateways that are Preczn, carry a merchant id, and
   * serve at least one payment type of the widget's kind, take the numerically lowest
   * CCGatewayID. None of those ⇒ null, and there is no fallback to the legacy client-config pick.
   */
  private static selectTokenizeGateway(
    resolution: GatewayResolutionDto,
    paymentTypeMatches: (paymentType: ResolvedPaymentTypeDto) => boolean
  ): TokenizeGatewayDto | null {
    const candidates = (resolution?.Gateways ?? []).filter(
      (gateway: ResolvedGatewayDto) =>
        gateway.IsPreczn === true && !!gateway.PrecznMerchantId && (gateway.PaymentTypes ?? []).some(paymentTypeMatches)
    );

    if (candidates.length === 0) {
      return null;
    }

    const selected = candidates.reduce((lowest, candidate) => (candidate.CCGatewayID < lowest.CCGatewayID ? candidate : lowest));

    return { ccGatewayId: selected.CCGatewayID, precznMerchantId: selected.PrecznMerchantId };
  }

  /** Keeps a re-resolution that names the same gateway from triggering a teardown. */
  private static gatewaysEqual(a: TokenizeGatewayDto | null, b: TokenizeGatewayDto | null): boolean {
    if (a === null || b === null) {
      return a === b;
    }
    return a.ccGatewayId === b.ccGatewayId && a.precznMerchantId === b.precznMerchantId;
  }
}
